import { Component, OnInit } from '@angular/core';
import { AppSessionStorageService } from 'src/app/services/utils/session-storage.service';
import { AuthserviceService } from 'src/app/services/authservice.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UndefinedNullableString } from 'src/app/type-alias';
import { ErrorMessageConstants } from 'src/app/constants/error-message-constants';
import { Router } from '@angular/router';

interface EditedDetails {
  firstname?: string;
  lastname?: string;
}
@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrl: './profile-edit.component.css',
})
export class ProfileEditComponent implements OnInit {
  editedProfileImage!: File;
  editProfileForm = new FormGroup({
    editedFirstName: new FormControl(''),
    editedLastName: new FormControl(''),
  });
  isSubmitting: boolean = false;
  errorString: UndefinedNullableString;
  preview: any;
  constructor(
    private authService: AuthserviceService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.editProfileForm = this.fb.group({
      editedFirstName: ['', [Validators.maxLength(32)]],
      editedLastName: ['', [Validators.maxLength(50)]],
    });
  }

  ngOnInit(): void {
    AppSessionStorageService.sideBarSelection = 2
    this.userDetails();
    this.setFormValues();
  }

  async userDetails() {
    if (!AppSessionStorageService.user) {
      await this.authService.fetchUserDetails();
    }
  }

  private setFormValues(): void {
    // Update the form control value when data is available
    this.editProfileForm.patchValue({
      editedFirstName: this.firstName,
      editedLastName: this.lastName,
    });
  }

  get firstName(): string {
    return AppSessionStorageService.user?.firstname ?? '';
  }

  get lastName(): string {
    return AppSessionStorageService.user?.lastname ?? '';
  }

  get profileImage(): string {
    return this.authService.getUserImage() ?? 'assets/default-image.svg';
  }

  async onSubmit() {
    this.errorString = '';
    if (!this.isSubmitting) {
      this.isSubmitting = true;
      try {
        const editedFirstName =
          this.editProfileForm.get('editedFirstName')!.value;
        const editedLastName =
          this.editProfileForm.get('editedLastName')!.value;
        if (this.validatingImageSize()) {
          return;
        }
        let editedDetails: EditedDetails = {};
        this.errorString = null;
        if (!this.editProfileForm.valid) {
          this.errorvalidator();
          return;
        }
        if (editedFirstName) {
          editedDetails.firstname = editedFirstName;
        }
        if (editedLastName) {
          editedDetails.lastname = editedLastName;
        }
        const response = await this.authService.editProfile(
          editedDetails,
          this.editedProfileImage
        );
        if (response?.success) {
          await this.authService.fetchUserDetails();
          this.profileView();
        } else {
          this.errorString = ErrorMessageConstants.SOMETHING_WRONG;
        }
      } catch (_) {
        this.errorString = ErrorMessageConstants.SOMETHING_WRONG;
      }
    }
    this.isSubmitting = false;
  }

  validatingImageSize(): UndefinedNullableString {
    // Check if file size exceeds 2MB
    if (
      this.editedProfileImage &&
      this.editedProfileImage.size > 2 * 1024 * 1024
    ) {
      this.errorString = 'File size cannot exceed more than 2MB.';
      return this.errorString;
    }
    return this.errorString;
  }

  async onFileSelected(event: any): Promise<void> {
    this.errorString = '';
    this.editedProfileImage = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.preview = e.target.result;
    };
    reader.readAsDataURL(this.editedProfileImage);
  }

  profileView() {
    AppSessionStorageService.grantAccess = null;
    if (AppSessionStorageService?.auth?.role == 'installer') {
      this.router.navigate(['installer/profile']);
    } else if (AppSessionStorageService?.auth?.role == 'administrator') {
      this.router.navigate(['admin/profile']);
    }
  }

  get editedFirstName() {
    return this.editProfileForm.get('editedFirstName');
  }

  get editedLastName() {
    return this.editProfileForm.get('editedLastName');
  }

  errorvalidator() {
    if (this.editedFirstName?.errors?.['maxlength']) {
      this.errorString = 'First name cannot exceed 10 characters.';
      return;
    } else if (this.editedLastName?.errors?.['maxlength']) {
      this.errorString = 'Last name cannot exceed 10 characters.';
      return;
    }
    this.errorString = 'Invalid request';
  }
}
